import ResetTemplate from 'src/components/atomic/template/Account/Reset'

import { RootState } from 'src/store/store'
import useResetEventBus from 'src/hooks/useEventBus/useResetEventBus'
import {
  ResetConfirmPin,
  ResetCreatePin,
  setConfirmPinHidden,
  setCreatePinHidden,
} from 'src/store/Reducer/page/resetPageSlice'

import { useAppSelector } from 'src/hooks/redux/useAppSelector'
import { useAppDispatch } from 'src/hooks/redux/useAppDispatch'
import { Cmdtypes } from 'src/constants/cmdtypes'
import { InputPropsTypeOption } from 'src/components/atomic/atom/Input'
import { useTranslation } from 'react-i18next'

import { InputBorderHandler } from 'src/utils/style.utils'
import { SvgType } from 'src/types/types'
import { CryptoUtils } from 'src/utils/Crypto.utils'
import { ResetErrText, setErrText } from 'src/store/Reducer/common/commonSlice'
import { commonUtils } from 'src/utils/common.utils'
import { useNavigate } from 'react-router-dom'

import { RequestDataBody } from 'src/types/websocket'
import { SocketSender } from 'src/api/socket'

type ResetItemsType = {
  text: string
  state: string
  inputOption: InputPropsTypeOption
  dispatch: () => {}
  border: string
  type: string
  svgName: SvgType
  svgStyle: { top: string; right: string; float: string; position: string }
  autoFocus: boolean
}
export type ResetDataPropsType = {
  ResetItemsList: ResetItemsType[]
  ResetBtnHandler: () => void
}

const ResetPage = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { ConfirmPinHidden, CreatePinHidden, CreatePin, ConfirmPin } = useAppSelector(
    (state: RootState) => state.ResetPage,
  )
  const { Email } = useAppSelector((state: RootState) => state.LoginPage)
  const { hasThreeConsecutiveDigits, hasThreeIdenticalDigits } = commonUtils
  const { errText } = useAppSelector((state: RootState) => state.common)

  const ResetItemsList: ResetItemsType[] = [
    {
      autoFocus: true,
      text: t('RESET_PIN.CREATE_INPUT_TITLE', { defaultValue: 'Create your New PIN' }),
      state: CreatePin,
      type: CreatePinHidden ? 'password' : 'text',
      inputOption: 'reset_create_pin',
      dispatch: () => dispatch(setCreatePinHidden(!CreatePinHidden)),
      border: InputBorderHandler(
        CreatePin?.length === 6,
        errText !== null || (CreatePin?.length !== 0 && CreatePin?.length < 6),
      ),
      svgName: CreatePinHidden ? 'pin_Input_eyes' : 'pin_Input_eyes_active',
      svgStyle: {
        top: CreatePinHidden ? '-32px' : '-34px',
        right: '17px',
        float: 'right',
        position: 'relative',
      },
    },
    {
      autoFocus: false,
      text: t('RESET_PIN.CONFIRM_INPUT_TITLE', { defaultValue: 'Confirm your New PIN' }),
      state: ConfirmPin,
      inputOption: 'reset_confirm_pin',
      type: ConfirmPinHidden ? 'password' : 'text',
      dispatch: () => dispatch(setConfirmPinHidden(!ConfirmPinHidden)),
      border: InputBorderHandler(
        ConfirmPin?.length === 6,
        errText !== null || (ConfirmPin?.length !== 0 && ConfirmPin?.length < 6),
      ),
      svgName: ConfirmPinHidden ? 'pin_Input_eyes' : 'pin_Input_eyes_active',
      svgStyle: {
        top: ConfirmPinHidden ? '-32px' : '-34px',
        right: '17px',
        float: 'right',
        position: 'relative',
      },
    },
  ]

  const ResetBtnHandler = () => {
    const pinReset = () => {
      dispatch(ResetCreatePin())
      dispatch(ResetConfirmPin())
    }

    switch (true) {
      case Email === '':
        navigate('/')
        pinReset()
        break
      case CreatePin !== ConfirmPin:
        dispatch(
          setErrText(
            t('RESET_PIN.ERROR_VERIFY', { defaultValue: 'The entered PINs are different.' }),
          ),
        )
        pinReset()
        break
      case hasThreeIdenticalDigits(ConfirmPin):
      case hasThreeConsecutiveDigits(ConfirmPin):
        dispatch(
          setErrText(
            t('RESET_PIN.ERROR_PATTERN', {
              defaultValue: 'Consecutive or repeated numbers of more than 3 digits cannot be used.',
            }),
          ),
        )
        pinReset()
        break
      default:
        dispatch(ResetErrText())
        dispatch(
          SocketSender<RequestDataBody.PinReset>(Cmdtypes.PIN_RESET, {
            VerifyPin: CryptoUtils.CreateVerifyPin(ConfirmPin, Email).cipher,
            Email: Email,
          }),
        )
        break
    }
  }

  useResetEventBus()

  const data: ResetDataPropsType = {
    ResetItemsList,
    ResetBtnHandler,
  }

  return <ResetTemplate data={data} />
}
export default ResetPage
